import { useState } from 'react';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { FormField, Input, Select, Textarea } from '../../../components/ui/Form';
import { Upload, FileText, AlertCircle } from 'lucide-react';
import { DECLARATION_TYPES, DECLARATION_CATEGORIES, PERIODICITY_LABELS } from '../../../types/declarations';
import type { DeclarationType, Declaration } from '../../../types/declarations';
import { useDeclarations } from '../../../hooks/useDeclarations';
import { useFiscalYear } from '../../../hooks/useFiscalYear';
import { useCurrency } from '../../../hooks/useCurrency';

interface NewDeclarationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  type: DeclarationType;
  period: string;
  amount: number;
  additionalFees: number;
  penalties: number;
  notes: string;
  justificationDocument: File | null;
  declarationForm: File | null;
}

export function NewDeclarationModal({ isOpen, onClose }: NewDeclarationModalProps) {
  const { currentFiscalYear } = useFiscalYear();
  const { addDeclaration } = useDeclarations({ fiscalYear: currentFiscalYear });
  const { currentCurrency } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    type: 'TVA',
    period: new Date().toISOString().slice(0, 7), // YYYY-MM format
    amount: 0,
    additionalFees: 0,
    penalties: 0,
    notes: '',
    justificationDocument: null,
    declarationForm: null
  });

  const selectedDeclarationType = DECLARATION_TYPES[formData.type];
  const periodicity = selectedDeclarationType.periodicity;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.justificationDocument) {
      alert('Le justificatif PDF est obligatoire');
      return;
    }

    setLoading(true);
    try {
      // Calculate due date based on declaration type
      const currentDate = new Date(formData.period);
      let dueDate: Date;
      
      const dueDateDay = selectedDeclarationType.dueDate as number;
      
      if (periodicity === 'monthly') {
        dueDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, dueDateDay);
      } else if (periodicity === 'quarterly') {
        const quarter = Math.floor(currentDate.getMonth() / 3);
        dueDate = new Date(currentDate.getFullYear(), (quarter + 1) * 3, dueDateDay);
      } else if (periodicity === 'annual') {
        dueDate = new Date(currentDate.getFullYear() + 1, 2, dueDateDay); // Mars
      } else {
        dueDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 6, dueDateDay);
      }

      const newDeclaration: Partial<Declaration> = {
        type: formData.type,
        period: formData.period,
        periodicity,
        dueDate: dueDate.toISOString(),
        amount: formData.amount,
        additionalFees: formData.additionalFees,
        penalties: formData.penalties,
        notes: formData.notes,
        // In a real app, files would be uploaded to server first
        justificationDocument: URL.createObjectURL(formData.justificationDocument),
        declarationForm: formData.declarationForm ? URL.createObjectURL(formData.declarationForm) : undefined
      };

      await addDeclaration(newDeclaration);
      onClose();
      
      // Reset form
      setFormData({
        type: 'TVA',
        period: new Date().toISOString().slice(0, 7),
        amount: 0,
        additionalFees: 0,
        penalties: 0,
        notes: '',
        justificationDocument: null,
        declarationForm: null
      });
    } catch (error) {
      console.error('Error creating declaration:', error);
      alert('Erreur lors de la création de la déclaration');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (field: 'justificationDocument' | 'declarationForm') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type === 'application/pdf') {
      setFormData(prev => ({ ...prev, [field]: file }));
    } else {
      alert('Veuillez sélectionner un fichier PDF');
    }
  };

  // Group declaration types by category
  const groupedDeclarationTypes = Object.entries(DECLARATION_TYPES).reduce((acc, [key, value]) => {
    if (!acc[value.category]) {
      acc[value.category] = [];
    }
    acc[value.category].push({ value: key, label: value.label });
    return acc;
  }, {} as Record<string, Array<{ value: string; label: string }>>);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Nouvelle Déclaration Fiscale"
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4 lg:space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          <FormField label="Type de déclaration" required>
            <Select
              value={formData.type}
              onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as DeclarationType }))}
              className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              {Object.entries(DECLARATION_CATEGORIES).map(([categoryKey, categoryLabel]) => (
                <optgroup key={categoryKey} label={categoryLabel}>
                  {groupedDeclarationTypes[categoryKey]?.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </Select>
            <div className="mt-2 text-xs lg:text-sm text-gray-600 dark:text-gray-400">
              <p><strong>Périodicité:</strong> {PERIODICITY_LABELS[periodicity]}</p>
              <p><strong>Taux:</strong> {selectedDeclarationType.rate}</p>
              <p><strong>Échéance:</strong> Le {selectedDeclarationType.dueDate as number} de la période suivante</p>
            </div>
          </FormField>

          <FormField label="Période" required>
            {periodicity === 'annual' ? (
              <Select
                value={formData.period.slice(0, 4)}
                onChange={(e) => setFormData(prev => ({ ...prev, period: e.target.value }))}
                className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                {Array.from({length: 5}, (_, i) => {
                  const year = new Date().getFullYear() - i;
                  return (
                    <option key={year} value={year.toString()}>
                      {year}
                    </option>
                  );
                })}
              </Select>
            ) : periodicity === 'quarterly' ? (
              <Select
                value={formData.period}
                onChange={(e) => setFormData(prev => ({ ...prev, period: e.target.value }))}
                className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                {Array.from({length: 4}, (_, i) => {
                  const year = new Date().getFullYear();
                  const quarter = i + 1;
                  const startMonth = (quarter - 1) * 3 + 1;
                  return (
                    <option key={`${year}-Q${quarter}`} value={`${year}-${startMonth.toString().padStart(2, '0')}`}>
                      Q{quarter} {year} (Mois {startMonth}-{startMonth + 2})
                    </option>
                  );
                })}
              </Select>
            ) : (
              <Input
                type="month"
                value={formData.period}
                onChange={(e) => setFormData(prev => ({ ...prev, period: e.target.value }))}
                max={new Date().toISOString().slice(0, 7)}
                className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />
            )}
          </FormField>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6">
          <FormField label={`Montant de la déclaration (${currentCurrency})`} required>
            <Input
              type="number"
              min="0"
              step="1"
              value={formData.amount}
              onChange={(e) => setFormData(prev => ({ ...prev, amount: Number(e.target.value) }))}
              placeholder="0"
              className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </FormField>

          <FormField label={`Frais supplémentaires non justifiés (${currentCurrency})`}>
            <Input
              type="number"
              min="0"
              step="1"
              value={formData.additionalFees}
              onChange={(e) => setFormData(prev => ({ ...prev, additionalFees: Number(e.target.value) }))}
              placeholder="0"
              className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </FormField>

          <FormField label={`Pénalités de retard (${currentCurrency})`}>
            <Input
              type="number"
              min="0"
              step="1"
              value={formData.penalties}
              onChange={(e) => setFormData(prev => ({ ...prev, penalties: Number(e.target.value) }))}
              placeholder="0"
              className="dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </FormField>
        </div>

        <div className="space-y-4">
          <FormField label="Justificatif de paiement (PDF)" required>
            <div className="flex items-center gap-3 lg:gap-4">
              <label className="flex items-center gap-2 px-3 lg:px-4 py-2 border border-dashed border-border rounded-lg cursor-pointer hover:border-primary bg-card">
                <Upload className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs lg:text-sm text-card-foreground">
                  {formData.justificationDocument ? formData.justificationDocument.name : 'Sélectionner un fichier PDF'}
                </span>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange('justificationDocument')}
                  className="hidden"
                />
              </label>
              {formData.justificationDocument && (
                <FileText className="h-5 w-5 text-green-600 dark:text-green-400" />
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Reçu de paiement ou justificatif bancaire au format PDF
            </p>
          </FormField>

          <FormField label="Formulaire de déclaration (PDF) - Optionnel">
            <div className="flex items-center gap-3 lg:gap-4">
              <label className="flex items-center gap-2 px-3 lg:px-4 py-2 border border-dashed border-border rounded-lg cursor-pointer hover:border-primary bg-card">
                <Upload className="h-4 w-4 text-muted-foreground" />
                <span className="text-xs lg:text-sm text-card-foreground">
                  {formData.declarationForm ? formData.declarationForm.name : 'Sélectionner un fichier PDF'}
                </span>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange('declarationForm')}
                  className="hidden"
                />
              </label>
              {formData.declarationForm && (
                <FileText className="h-5 w-5 text-green-600 dark:text-green-400" />
              )}
            </div>
          </FormField>
        </div>

        <FormField label="Notes et observations">
          <Textarea
            value={formData.notes}
            onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
            placeholder="Informations complémentaires sur cette déclaration..."
            rows={3}
            className="dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
          />
        </FormField>

        <div className="bg-blue-50 dark:bg-blue-900/20 p-3 lg:p-4 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="text-xs lg:text-sm text-blue-800 dark:text-blue-300">
              <p className="font-medium mb-1">Information sur {selectedDeclarationType.label}</p>
              <p>{selectedDeclarationType.description}</p>
              <p className="mt-1"><strong>Échéance:</strong> Le {selectedDeclarationType.dueDate as number} de chaque {PERIODICITY_LABELS[periodicity].toLowerCase()}</p>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
            className="w-full sm:w-auto order-2 sm:order-1"
          >
            Annuler
          </Button>
          <Button 
            type="submit"
            variant="primary"
            isLoading={loading}
            className="w-full sm:w-auto order-1 sm:order-2"
          >
            Créer la déclaration
          </Button>
        </div>
      </form>
    </Modal>
  );
}